import { useState, useEffect } from "react";
import type {
  SpendingGoalWithProgress,
  WorkspaceMemberWithUser,
  LogCategory,
  LogMethod,
} from "@repo/share-log-interfaces";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@web/components/ui/dialog";
import { Button } from "@web/components/ui/button";
import { Input } from "@web/components/ui/input";
import { Label } from "@web/components/ui/label";
import { Spinner } from "@web/components/ui/spinner";
import { toastService } from "@web/features/toast/toast-service";
import { useSpendingGoalStore } from "@web/stores";
import { logger } from "@web/lib/logger";
import { cn } from "@repo/share-log-web-utils";

interface SpendingGoalFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal?: SpendingGoalWithProgress;
  workspaceId: number;
  members: WorkspaceMemberWithUser[];
  categories: LogCategory[];
  methods: LogMethod[];
  currentUserId: number;
}

function formatNumberWithCommas(value: string): string {
  const num = value.replace(/[^\d]/g, "");
  if (!num) return "";
  return Number(num).toLocaleString("ko-KR");
}

function parseFormattedNumber(value: string): number {
  return Number(value.replace(/[^\d]/g, "")) || 0;
}

export function SpendingGoalFormDialog({
  open,
  onOpenChange,
  goal,
  workspaceId,
  members,
  categories,
  methods,
  currentUserId,
}: SpendingGoalFormDialogProps) {
  const { createGoal, updateGoal } = useSpendingGoalStore();
  const isEdit = !!goal;

  const [name, setName] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedMethodIds, setSelectedMethodIds] = useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (goal) {
        setName(goal.name);
        setTargetAmount(goal.targetAmount.toLocaleString("ko-KR"));
        setSelectedCategoryIds([...goal.categoryIds]);
        setSelectedMethodIds([...goal.methodIds]);
        setSelectedUserIds([...goal.userIds]);
      } else {
        setName("");
        setTargetAmount("");
        setSelectedCategoryIds([]);
        setSelectedMethodIds([]);
        setSelectedUserIds([currentUserId]);
      }
    }
  }, [open, goal, currentUserId]);

  const toggleId = (
    ids: number[],
    setIds: React.Dispatch<React.SetStateAction<number[]>>,
    id: number,
  ) => {
    setIds(ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseFormattedNumber(targetAmount);
    if (!name.trim()) {
      toastService.warning("목표 이름을 입력해주세요.");
      return;
    }
    if (parsedAmount <= 0) {
      toastService.warning("목표 금액을 입력해주세요.");
      return;
    }

    setIsSubmitting(true);
    try {
      const dto = {
        name: name.trim(),
        targetAmount: parsedAmount,
        categoryIds: selectedCategoryIds,
        methodIds: selectedMethodIds,
        userIds: selectedUserIds,
      };

      if (isEdit) {
        await updateGoal(workspaceId, goal.id, dto);
        toastService.success("지출 목표가 수정되었습니다.");
      } else {
        await createGoal(workspaceId, dto);
        toastService.success("지출 목표가 생성되었습니다.");
      }
      onOpenChange(false);
    } catch (error) {
      logger.error(error);
      toastService.error(
        isEdit
          ? "지출 목표 수정에 실패했습니다."
          : "지출 목표 생성에 실패했습니다.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "지출 목표 수정" : "새 지출 목표"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "지출 목표를 수정합니다."
              : "이번 달 지출 목표를 설정합니다."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* 목표 이름 */}
          <div className="space-y-2">
            <Label htmlFor="goal-name">목표 이름</Label>
            <Input
              id="goal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 이번 달 식비 목표"
              required
            />
          </div>

          {/* 목표 금액 */}
          <div className="space-y-2">
            <Label htmlFor="goal-amount">목표 금액 (원)</Label>
            <Input
              id="goal-amount"
              type="text"
              inputMode="numeric"
              value={targetAmount}
              onChange={(e) =>
                setTargetAmount(formatNumberWithCommas(e.target.value))
              }
              placeholder="예: 500,000"
              required
            />
          </div>

          {/* 카테고리 필터 */}
          {categories.length > 0 && (
            <div className="space-y-2">
              <Label>카테고리</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategoryIds([])}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    selectedCategoryIds.length === 0
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background hover:bg-accent",
                  )}
                >
                  전체
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() =>
                      toggleId(selectedCategoryIds, setSelectedCategoryIds, cat.id)
                    }
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                      selectedCategoryIds.includes(cat.id)
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background hover:bg-accent",
                    )}
                  >
                    {cat.color && (
                      <span
                        className="inline-block size-2.5 rounded-full"
                        style={{ backgroundColor: cat.color }}
                      />
                    )}
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 수단 필터 */}
          {methods.length > 0 && (
            <div className="space-y-2">
              <Label>수단</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedMethodIds([])}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    selectedMethodIds.length === 0
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background hover:bg-accent",
                  )}
                >
                  전체
                </button>
                {methods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() =>
                      toggleId(selectedMethodIds, setSelectedMethodIds, method.id)
                    }
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                      selectedMethodIds.includes(method.id)
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background hover:bg-accent",
                    )}
                  >
                    {method.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 사용자 필터 */}
          {members.length > 0 && (
            <div className="space-y-2">
              <Label>사용자</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedUserIds([])}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    selectedUserIds.length === 0
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background hover:bg-accent",
                  )}
                >
                  전체
                </button>
                {members.map((member) => (
                  <button
                    key={member.userId}
                    type="button"
                    onClick={() =>
                      toggleId(selectedUserIds, setSelectedUserIds, member.userId)
                    }
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                      selectedUserIds.includes(member.userId)
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background hover:bg-accent",
                    )}
                  >
                    {member.user.nickname ?? member.user.email}
                  </button>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  저장 중...
                </>
              ) : isEdit ? (
                "수정"
              ) : (
                "생성"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
